/**
 * Post-processing pass — reads the HDR scene texture, applies a cheap
 * night-gated bloom, ACES tonemap (Stephen Hill fit), a soft vignette, and a
 * subtle cool shadow tint, then writes to the swapchain. Single fullscreen
 * triangle, no depth attachment.
 *
 * Bind groups (consistent with the rest of the renderer):
 *   group(0) = camera (set by frame.ts)        — viewport for UV/NDC math
 *   group(1) = post-private (HDR view + samp)  — rebuilt on resize
 *   group(2) = daynight (set by frame.ts)      — sun elevation gates bloom
 */

import { createRenderPipeline } from '../gpu/pipelines.ts';
import wgsl from './post.wgsl?raw';

export interface PostPass {
  draw(pass: GPURenderPassEncoder): void;
  onResize(newHdrView: GPUTextureView): void;
  destroy(): void;
}

export interface PostPassOptions {
  swapchainFormat: GPUTextureFormat;
  hdrFormat: GPUTextureFormat;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
  initialHdrView: GPUTextureView;
}

export function createPostPass(device: GPUDevice, opts: PostPassOptions): PostPass {
  const sampler = device.createSampler({
    label: 'post.samp',
    magFilter: 'linear',
    minFilter: 'linear',
    addressModeU: 'clamp-to-edge',
    addressModeV: 'clamp-to-edge',
  });

  const postBgl = device.createBindGroupLayout({
    label: 'post.bgl',
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: 'float', viewDimension: '2d' },
      },
      {
        binding: 1,
        visibility: GPUShaderStage.FRAGMENT,
        sampler: { type: 'filtering' },
      },
    ],
  });

  const pipelineLayout = device.createPipelineLayout({
    label: 'post.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      postBgl,
      opts.dayNightBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'post',
    layout: pipelineLayout,
    targets: [{ format: opts.swapchainFormat }],
    primitive: { topology: 'triangle-list', cullMode: 'none' },
  });

  const buildBindGroup = (hdrView: GPUTextureView): GPUBindGroup =>
    device.createBindGroup({
      label: 'post.bg',
      layout: postBgl,
      entries: [
        { binding: 0, resource: hdrView },
        { binding: 1, resource: sampler },
      ],
    });

  let postBg = buildBindGroup(opts.initialHdrView);

  return {
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, postBg);
      pass.draw(3);
    },
    onResize(newHdrView) {
      postBg = buildBindGroup(newHdrView);
    },
    destroy() {
      // Sampler, bind group layout, and pipeline are GC'd with the device.
    },
  };
}
